import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { putUserConfig, type UserConfigDto } from "../../services/userConfig";
import type { TempUnit } from "../../services/cookingState";
import UserConfigEditor from "./UserConfigEditor";
import { isAmbientBandValid } from "./HomeTempThresholdEditor";
import Button from "./Button";
import IconButton from "./IconButton";
import CloseIcon from "@mui/icons-material/Close";

type Target = { name: string; displayName?: string };

type Props = {
  open: boolean;
  onClose: () => void;
  buildingName?: string;
  targets: Target[];
  unit: TempUnit;
};

type Result = {
  name: string;
  displayName?: string;
  status: "sent" | "failed";
  error?: string;
  /**
   * HTTP status of the failure, because 502 means something different from the
   * rest: PutUserConfig persists the row and THEN publishes, so a 502 ("saved
   * but publish to device failed") did write the cloud row while 400/404/500
   * did not. Collapsing them made the modal tell a user their change was stored
   * when nothing had been.
   */
  httpStatus?: number;
};

/**
 * Bulk-apply a partial UserConfig to every sensor at a property.
 * Same modal shell + per-sensor progress feedback as
 * `BuildingScheduleModal` and the earlier `BuildingHomeTempModal`.
 *
 * Uses partial-update semantics: only sections the user explicitly
 * ticks in the editor contribute to the PUT body. Sensors keep every
 * unchanged field on both the server and the device -- so a bulk
 * "everyone's unattended speed to slow" doesn't wipe individually-tuned
 * ambient thresholds or speaker volumes at the same time.
 */
export default function BuildingUserConfigModal({
  open,
  onClose,
  buildingName,
  targets,
  unit,
}: Props) {
  const queryClient = useQueryClient();
  const [patch, setPatch] = useState<UserConfigDto>({});
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);

  // Shared with HomeTempThresholdEditor rather than re-derived here. This used
  // to carry its own `lowC >= highC` copy gated on `enabled === true`, which
  // meant a band set while switched OFF could be saved invalid and turned on
  // later. Everything else in the DTO is bool or bounded by its own control.
  const invalid =
    !!patch.ambientAlerts &&
    !isAmbientBandValid(patch.ambientAlerts.lowC, patch.ambientAlerts.highC);

  const nothingToApply = Object.keys(patch).length === 0;

  // "Dirty" = the user has ticked at least one section, so a close would
  // discard in-flight configuration work. Once results are showing (post-
  // apply) the patch has been sent; nothing left to lose from closing.
  const dirty = !nothingToApply && !results;

  const confirmDiscardIfDirty = (): boolean => {
    if (!dirty) return true;
    return window.confirm(
      "Discard your unsaved changes?\n\n" +
        "You've picked settings to change but haven't applied them yet. " +
        "Closing will lose your selections."
    );
  };

  const requestClose = () => {
    if (applying) return;
    if (confirmDiscardIfDirty()) onClose();
  };

  // Reset every time the modal opens so a previous session's draft
  // doesn't silently carry over.
  useEffect(() => {
    if (open) {
      setPatch({});
      setApplying(false);
      setResults(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !applying) {
        if (confirmDiscardIfDirty()) onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // confirmDiscardIfDirty is re-created on every render, so we rely on
    // the closure reading fresh state each keydown instead of listing it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, applying, dirty]);

  // Guard tab close / reload / navigation away from the tab entirely.
  // In-SPA route changes (clicking a different page) are NOT caught
  // here -- react-router-dom 6.2 doesn't ship useBlocker on the non-
  // data router this app uses. Since this is a modal, closing the
  // parent page from under it is rare in practice.
  useEffect(() => {
    if (!open || !dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers used the return value; modern ones ignore the
      // string and show their generic prompt, but returning "" keeps
      // the guard fired across both.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [open, dirty]);

  if (!open) return null;

  const applyToAll = async () => {
    setApplying(true);
    const collected: Result[] = [];
    for (const t of targets) {
      try {
        await putUserConfig(t.name, patch);
        collected.push({
          name: t.name,
          displayName: t.displayName,
          status: "sent",
        });
      } catch (err) {
        collected.push({
          name: t.name,
          displayName: t.displayName,
          status: "failed",
          error: err instanceof Error ? err.message : String(err),
          httpStatus: (err as { response?: { status?: number } })?.response?.status,
        });
      }
      setResults([...collected]);
    }

    // Invalidate AFTER the fan-out, by key prefix so every sensor is covered
    // without enumerating them.
    //
    // This is load-bearing now in a way it was not before. SensorUserConfigCard
    // carries staleTime: Infinity (added to stop the editor being re-seeded
    // mid-edit), so a card whose data is already cached will NEVER refetch on
    // its own. Without this, bulk-applying and then opening a sensor viewed
    // earlier in the session shows the PRE-BULK values, and saving from there
    // writes them back over the bulk change -- to the device.
    //
    // Failed sensors are invalidated too: a 502 wrote the cloud row, so their
    // cached entry is stale as well.
    queryClient.invalidateQueries({ queryKey: ["userConfig"] });
    queryClient.invalidateQueries({ queryKey: ["homeTemperatureThreshold"] });

    setApplying(false);
  };

  // "Sent", not "Applied". A 200 means the cloud row was written and AWS IoT
  // accepted the publish -- which is Qos = 0, fire-and-forget, so it is fully
  // compatible with the device never receiving the command. There is no device
  // confirmation available on this path by design (see putUserConfig's comment:
  // the bulk fan-out is why the ack poll was dropped).
  const sentCount = results?.filter((r) => r.status === "sent").length ?? 0;
  const failedCount =
    results?.filter((r) => r.status === "failed").length ?? 0;
  // 502 is the one failure that DID write the cloud row -- PutUserConfig
  // persists then publishes -- so it is the only case where "saved" is true.
  const savedNotDeliveredCount =
    results?.filter((r) => r.status === "failed" && r.httpStatus === 502)
      .length ?? 0;
  const done = results != null && !applying;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Configure all sensors"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={requestClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-card bg-white text-ink p-6 shadow-depth border border-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-xl font-semibold">Configure Sensors</h2>
          <IconButton
            label="Close"
            onClick={requestClose}
            disabled={applying}
            className="!w-8 !h-8 !bg-black/[0.04] !text-ink !border-black/10 hover:!bg-black/10"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <p className="text-ink-3 text-sm mb-5">
          Tick the settings you want to change, then apply to{" "}
          <b>
            {targets.length} sensor{targets.length === 1 ? "" : "s"}
          </b>
          {buildingName ? ` in ${buildingName}` : ""}. Settings you leave
          unticked are preserved on each sensor.
        </p>

        {results ? (
          <div>
            <div className="mb-4 flex flex-wrap gap-3 text-sm">
              <span className="rounded-chip bg-alert-success/10 text-alert-success px-3 py-1 font-medium">
                {sentCount} Sent
              </span>
              {failedCount > 0 && (
                <span className="rounded-chip bg-alert-critical/10 text-alert-critical px-3 py-1 font-medium">
                  {failedCount} Failed
                </span>
              )}
              {applying && (
                <span className="text-ink-3">
                  Applying… ({results.length}/{targets.length})
                </span>
              )}
            </div>
            <ul className="space-y-1 max-h-64 overflow-y-auto">
              {results.map((r) => (
                <li
                  key={r.name}
                  className="flex items-center justify-between text-sm border-b border-black/5 py-1.5"
                >
                  <span className="font-medium">
                    {r.displayName || r.name}
                  </span>
                  <span
                    className={
                      r.status === "sent"
                        ? "text-alert-success"
                        : "text-alert-critical"
                    }
                    title={r.error}
                  >
                    {r.status === "sent" ? "Sent" : "Failed"}
                  </span>
                </li>
              ))}
            </ul>
            {done && failedCount > 0 && (
              <p className="text-ink-3 text-xs mt-3">
                {savedNotDeliveredCount > 0 && (
                  <>
                    {savedNotDeliveredCount} saved in the cloud but not
                    delivered to the device.{" "}
                  </>
                )}
                Failed sensors were not updated. Re-run this once they&rsquo;re
                online &mdash; nothing re-sends automatically.
              </p>
            )}
            {done && (
              <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-black/10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setResults(null)}
                  className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
                >
                  Edit Settings
                </Button>
                <Button variant="primary" size="sm" onClick={onClose}>
                  Done
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <UserConfigEditor
              onChange={setPatch}
              unit={unit}
            />
            <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-black/10">
              <Button
                variant="ghost"
                size="sm"
                onClick={requestClose}
                className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={applyToAll}
                disabled={
                  targets.length === 0 || invalid || nothingToApply
                }
                title={
                  nothingToApply
                    ? "Tick at least one setting to change"
                    : undefined
                }
              >
                Apply to All
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
